import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, uploadFile } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, Download, Upload } from "lucide-react";
import { SiswaForm, emptySiswa } from "./SiswaForm";
import { SiswaDetailModal } from "./SiswaDetailModal";
import { KETUNAAN_LIST } from "@/lib/constants";
import { USIA_FILTER_OPTIONS, hitungUsia } from "@/lib/api";

function ExcelControls({ onReload }) {
  const [busy, setBusy] = React.useState(false);
  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/excel/siswa/template`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/siswa/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted}, lewat duplikat: ${data.skipped_duplicate}`);
      if (data.errors?.length) toast.error(`Error: ${data.errors.join(" | ")}`);
      onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex gap-2 mb-4">
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="dl-template-siswa">
        <Download className="w-4 h-4" /> Download Template Excel
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="upload-siswa-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Excel Siswa (Banyak)"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}

export default function AdminSiswa() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [jk, setJk] = useState("");
  const [status, setStatus] = useState("");
  const [ketunaan, setKetunaan] = useState("");
  const [usia, setUsia] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptySiswa);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [applied, setApplied] = useState({ q: "", jk: "", status: "", ketunaan: "", usia: "" });
  const load = useCallback(async () => {
    try {
      const params = {};
      if (applied.q) params.q = applied.q;
      if (applied.jk) params.jenis_kelamin = applied.jk;
      if (applied.status) params.status = applied.status;
      if (applied.usia) params.usia = applied.usia;
      const { data } = await api.get("/siswa", { params });
      // Client-side filter for ketunaan
      const filtered = applied.ketunaan
        ? data.filter((s) => (s.ketunaan || "") === applied.ketunaan)
        : data;
      setItems(filtered);
      const { data: st } = await api.get("/siswa/stats");
      setStats(st);
    } catch (e) { console.error("Load siswa failed:", e); }
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const apply = () => setApplied({ q, jk, status, ketunaan, usia });
  const reset = () => { setQ(""); setJk(""); setStatus(""); setKetunaan(""); setUsia(""); setApplied({ q: "", jk: "", status: "", ketunaan: "", usia: "" }); };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const res = await uploadFile(f); setForm({ ...form, foto_file_id: res.id }); toast.success("Foto diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/siswa/${editId}`, form);
      else await api.post("/siswa", form);
      toast.success(editId ? "Data diperbarui" : "Siswa ditambahkan");
      setShowForm(false); setForm(emptySiswa); setEditId(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const startEdit = (s) => { setForm({ ...emptySiswa, ...s }); setEditId(s.id); setShowForm(true); };
  const cancelForm = () => { setShowForm(false); setForm(emptySiswa); setEditId(null); };
  const del = async (id) => {
    if (!window.confirm("Hapus siswa ini?")) return;
    await api.delete(`/siswa/${id}`); toast.success("Dihapus"); load();
  };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Kelola Data Siswa" action={
        <button onClick={() => { setShowForm(true); setForm(emptySiswa); setEditId(null); }} className="pp-btn-primary" data-testid="open-create-siswa">
          <Plus className="w-4 h-4" /> Input Siswa Baru
        </button>
      } />

      <ExcelControls onReload={load} />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Siswa Aktif", value: stats.aktif ?? 0 },
          { label: "Siswa Tidak Aktif", value: stats.tidak_aktif ?? 0 },
          { label: "Perempuan Aktif", value: stats.perempuan_aktif ?? 0 },
          { label: "Laki-laki Aktif", value: stats.laki_aktif ?? 0 },
        ].map((s) => (
          <div key={s.label} className="pp-card p-4">
            <div className="text-xs text-slate-500">{s.label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{s.value}</div>
          </div>
        ))}
      </section>

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <input className="pp-input" placeholder="Cari nama siswa" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-q" />
          <select className="pp-input" value={jk} onChange={(e) => setJk(e.target.value)} data-testid="filter-jk">
            <option value="">Semua jenis kelamin</option>
            <option value="L">Laki-laki</option>
            <option value="P">Perempuan</option>
          </select>
          <select className="pp-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
            <option value="">Semua status</option>
            <option value="aktif">Aktif</option>
            <option value="tidak_aktif">Tidak Aktif</option>
          </select>
          <select className="pp-input" value={ketunaan} onChange={(e) => setKetunaan(e.target.value)} data-testid="filter-ketunaan">
            <option value="">Semua ketunaan</option>
            {KETUNAAN_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select className="pp-input" value={usia} onChange={(e) => setUsia(e.target.value)} data-testid="filter-usia">
            <option value="">Semua usia</option>
            {USIA_FILTER_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <div className="flex gap-2">
            <button onClick={apply} className="pp-btn-primary flex-1" data-testid="apply-filter">Terapkan</button>
            <button onClick={reset} className="pp-btn-secondary flex-1" data-testid="reset-filter">Reset</button>
          </div>
        </div>
      </section>

      {showForm && (
        <SiswaForm
          form={form} setForm={setForm} editId={editId}
          onSubmit={submit} onCancel={cancelForm}
          onUpload={handleUpload} uploading={uploading}
        />
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="siswa-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Nama</th>
              <th className="text-left px-5 py-3">NISN</th>
              <th className="text-left px-5 py-3">JK</th>
              <th className="text-left px-5 py-3">Usia</th>
              <th className="text-left px-5 py-3">Ketunaan</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada data siswa.</td></tr>}
              {items.map((s) => {
                const u = s.usia ?? hitungUsia(s.tanggal_lahir);
                return (
                <tr key={s.id} className="pp-row" data-testid={`siswa-row-${s.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{s.nama}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-600">{s.nis}</td>
                  <td className="px-5 py-3">{s.jenis_kelamin === "L" ? "Laki-laki" : "Perempuan"}</td>
                  <td className="px-5 py-3 text-slate-700" data-testid={`siswa-usia-${s.id}`}>{u == null ? "—" : `${u} th`}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{s.ketunaan || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={s.status === "aktif" ? "pp-badge-success" : "pp-badge-danger"}>{s.status === "aktif" ? "Aktif" : "Tidak Aktif"}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setDetail(s)} className="pp-btn-ghost text-xs" data-testid={`detail-${s.id}`}><Eye className="w-3.5 h-3.5" />Detail</button>
                      <button onClick={() => startEdit(s)} className="pp-btn-ghost text-xs" data-testid={`edit-${s.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                      <button onClick={() => del(s.id)} className="pp-btn-danger" data-testid={`delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <SiswaDetailModal detail={detail} onClose={() => setDetail(null)} />
    </Layout>
  );
}
